import React, { useState, useRef, useEffect, useMemo } from 'react';
import { getTimezones, getTimezoneOffsetLabel, getTimezoneSearchTerms, getTimezoneOffsetMinutes } from '../utils/timezones';
import './TimezoneSelect.css';

export interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  className?: string;
}

export const TimezoneSelect: React.FC<TimezoneSelectProps> = ({
  value,
  onChange,
  disabled = false,
  id,
  placeholder = 'Select timezone',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => {
    const list = getTimezones().map((tz) => {
      const label = `${getTimezoneOffsetLabel(tz)}  ${tz}`;
      const searchTerms = getTimezoneSearchTerms(tz).join(' ');
      const searchText = `${label} ${searchTerms}`.toLowerCase();
      return { value: tz, label, searchText };
    });
    list.sort((a, b) => getTimezoneOffsetMinutes(a.value) - getTimezoneOffsetMinutes(b.value));
    return list;
  }, []);

  const filteredOptions = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return options;
    const filtered = options.filter((o) => o.searchText.includes(q));
    const hasSelected = filtered.some((o) => o.value === value);
    if (!hasSelected && value) {
      const label = `${getTimezoneOffsetLabel(value)}  ${value}`;
      return [{ value, label }, ...filtered];
    }
    return filtered;
  }, [options, filterQuery, value]);

  const selectedLabel = value ? `${getTimezoneOffsetLabel(value)}  ${value}` : '';

  useEffect(() => {
    if (!isOpen) return;
    setFilterQuery('');
    const t = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleSelect = (tz: string) => {
    onChange(tz);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`timezone-select ${isOpen ? 'timezone-select--open' : ''} ${disabled ? 'timezone-select--disabled' : ''} ${className}`}
    >
      <button
        type="button"
        id={id}
        className="timezone-select__trigger"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Timezone"
      >
        <span className="timezone-select__trigger-text">
          {selectedLabel || placeholder}
        </span>
        <span className="timezone-select__chevron" aria-hidden>▼</span>
      </button>

      {isOpen && (
        <div className="timezone-select__dropdown" role="listbox">
          <div className="timezone-select__filter-wrap">
            <input
              ref={inputRef}
              type="text"
              className="timezone-select__filter"
              placeholder="Filter (e.g. America, GMT+3...)"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Filter timezone"
            />
          </div>
          <div className="timezone-select__list">
            {filteredOptions.length === 0 ? (
              <div className="timezone-select__empty">No timezone found</div>
            ) : (
              filteredOptions.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  className={`timezone-select__option ${o.value === value ? 'timezone-select__option--selected' : ''}`}
                  onClick={() => handleSelect(o.value)}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimezoneSelect;
